import {
  Avatar,
  AvatarGroup,
  Box,
  Card,
  CardActionArea,
  Typography
} from '@mui/material'
import { styled } from '@mui/material/styles'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FormRow } from 'src/generated-types/form-field'
import { MUIThemeColors } from 'src/mui-theme'
import Form from 'src/screens/customer/form/Form'
import { formSchemaHeaderGradient } from 'src/screens/customer/form/formSchemaDashboardHelpers'
import type { FormSchemaDashboardRow } from 'src/screens/customer/form/formSchemaDashboardTypes'
import {
  parseFormSchemaLayoutForPreview
} from 'src/screens/customer/form/formSchemaPreviewDialog'
import { getThemeColor } from 'src/utils'

const PREVIEW_SCALE = 0.5

const noop = () => { }

export interface FormSchemaPickerCardProps {
  schema: FormSchemaDashboardRow
  onSelect: () => void
}

export const FormSchemaPickerCard: React.FC<FormSchemaPickerCardProps> = ({ schema, onSelect }) => {
  const { t } = useTranslation(['common', 'form-schema'])
  const id = schema.id ?? 0
  const collaborators = schema.collaborators ?? []

  const layoutPreview = useMemo(
    (): FormRow[] | null => parseFormSchemaLayoutForPreview(schema.version?.layout),
    [schema.version?.layout],
  )

  return (
    <SchemaPickerCardRoot>
      <SchemaPickerCardAction onClick={onSelect}>
        <SchemaPickerMedia>
          {layoutPreview ? (
            <SchemaPickerPreviewStage>
              <SchemaPickerPreviewScaled>
                <SchemaPickerPreviewFormSurface>
                  <Form
                    form={undefined}
                    title=""
                    layouts={layoutPreview}
                    disabled={false}
                    previewMode
                    withColorLayout
                    density="compact"
                    onDrop={noop}
                    addLayout={noop}
                    deleteLayout={noop}
                    selectFormField={noop}
                    onRearrange={undefined}
                    onContentChange={undefined}
                    cancelEnabled={false}
                  />
                </SchemaPickerPreviewFormSurface>
              </SchemaPickerPreviewScaled>
            </SchemaPickerPreviewStage>
          ) : (
            <SchemaPickerMediaFallback headerImageUrl={schema.headerImage} gradientSeed={id} />
          )}
        </SchemaPickerMedia>

        <SchemaPickerBody>
          <Typography variant="subtitle1" fontWeight={700} noWrap title={schema.name}>
            {schema.name}
          </Typography>
          <SchemaPickerDescription variant="body2" color="text.secondary">
            {schema.description?.trim() ? schema.description : '—'}
          </SchemaPickerDescription>

          {collaborators.length > 0 && (
            <SchemaPickerCollaboratorsRow>
              <SchemaPickerAvatarGroup max={4}>
                {collaborators.map((c, i) => (
                  <Avatar key={`${c.userName}-${i}`} alt={c.userName} src={c.userImageUrl}>
                    {c.userName?.charAt(0)?.toUpperCase()}
                  </Avatar>
                ))}
              </SchemaPickerAvatarGroup>
            </SchemaPickerCollaboratorsRow>
          )}
        </SchemaPickerBody>
      </SchemaPickerCardAction>
    </SchemaPickerCardRoot>
  )
}

/* Styled components (alphabetical) */

const SchemaPickerAvatarGroup = styled(AvatarGroup)({
  '& .MuiAvatar-root': {
    width: 26,
    height: 26,
    fontSize: 11,
  },
})

const SchemaPickerBody = styled(Box)({
  padding: 14,
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
})

const SchemaPickerCardAction = styled(CardActionArea)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  flex: 1,
  textAlign: 'left',
})

const SchemaPickerCardRoot = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.spacing(2),
  overflow: 'hidden',
  boxShadow: theme.shadows[2],
  backgroundColor: getThemeColor(theme, MUIThemeColors.white),
}))

const SchemaPickerCollaboratorsRow = styled(Box)({
  marginTop: 12,
  display: 'flex',
  justifyContent: 'flex-end',
})

const SchemaPickerDescription = styled(Typography)({
  marginTop: 6,
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  minHeight: 54,
})

const SchemaPickerMedia = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: 128,
  flexShrink: 0,
  overflow: 'hidden',
  backgroundColor: getThemeColor(theme, MUIThemeColors.infoLight),
}))

const SchemaPickerMediaFallback = styled(Box, {
  shouldForwardProp: prop => prop !== 'headerImageUrl' && prop !== 'gradientSeed',
})<{ headerImageUrl?: string; gradientSeed: number }>(({ headerImageUrl, gradientSeed }) => ({
  position: 'absolute',
  inset: 0,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  background: headerImageUrl
    ? `linear-gradient(0deg, rgba(0,0,0,0.35), rgba(0,0,0,0.15)), url(${headerImageUrl})`
    : formSchemaHeaderGradient(gradientSeed),
}))

const SchemaPickerPreviewFormSurface = styled(Box)(({ theme }) => ({
  backgroundColor: getThemeColor(theme, MUIThemeColors.white),
}))

const SchemaPickerPreviewScaled = styled(Box)({
  width: `calc(100% / ${PREVIEW_SCALE})`,
  transform: `scale(${PREVIEW_SCALE})`,
  transformOrigin: 'top center',
})

const SchemaPickerPreviewStage = styled(Box)({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  justifyContent: 'center',
  pointerEvents: 'none',
})
